import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { useNavigationGuard } from "@/context/NavigationGuard";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, CalendarCheck2, AlertTriangle, CheckCircle2, Clock, Ban, CalendarPlus, Sparkles, Repeat, ChevronLeft, ChevronRight, Eraser, Pencil, X, Trash2, PauseCircle } from "lucide-react";
import { attendanceChipClass, attendanceDotClass } from "@/lib/attendanceColors";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDayLabel, currentCycleDates, pauseOn } from "@/lib/schedule";
import ExtraSessionDialog from "@/components/ExtraSessionDialog";
import { todayISO, formatDMY } from "@/lib/dateFormat";

const STATUSES = ["present", "justified", "unjustified", "teacher_cancelled"];
// Legacy "absent" status (without justification) is still accepted from old records
// but is no longer offered as a new option. Use "unjustified" instead.
const WEEKDAY_TO_CODE = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]; // JS getDay()

export default function TeacherAttendance() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canMarkNotCounted = user?.role === "superadmin" || user?.role === "academic_director";
  const canRemoveFromPlan = user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";
  const { setGuard, pending, proceed, cancel } = useNavigationGuard();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [groupId, setGroupId] = useState(params.get("group") || "");
  const [date, setDate] = useState(todayISO());
  const [records, setRecords] = useState({}); // student_id -> status
  const [notes, setNotes] = useState({});
  const [reportedDates, setReportedDates] = useState(new Set()); // ISO dates already reported for current group
  const [groupAttendance, setGroupAttendance] = useState([]); // full attendance rows for the group (for cycle classification)
  const [cycleOffset, setCycleOffset] = useState(0);
  const [dirty, setDirty] = useState(false);
  const baselineRef = useRef({ records: {}, notes: {} });
  const [pendingNav, setPendingNav] = useState(null); // {type:'date'|'group'|'cancel', value, exec}
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraSessions, setExtraSessions] = useState([]);
  const [clearTarget, setClearTarget] = useState(null); // {student_id, name}
  // Cancelaciones hechas por el alumno desde la app: mapa "student|fecha" → detalle.
  const [cancellations, setCancellations] = useState({});
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [overrideWarn, setOverrideWarn] = useState(null); // {student_id, status, cancellation}
  // Cuándo avisó el estudiante (hora de Costa Rica): con eso se mide la política.
  const [notifiedAt, setNotifiedAt] = useState({});   // student_id -> "YYYY-MM-DDTHH:MM" | ""
  const [notifyAsk, setNotifyAsk] = useState(null);   // {student_id, when}
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // {date, existing}
  const [undoTarget, setUndoTarget] = useState(null); // extra_session doc
  const snapDateRef = useRef(false); // true = la fecha debe saltar al ciclo mostrado
  // Filtro por profesor: le sirve al admin para encontrar el grupo rápido
  // cuando tiene que corregir una asistencia. La profe no lo ve (solo tiene
  // sus propios grupos).
  const isAcademicStaff = user?.role === "superadmin" || user?.role === "academic_director";
  const [teacherFilter, setTeacherFilter] = useState("all");

  const reloadExtrasAndAttendance = async () => {
    try {
      const [rEx, rAtt] = await Promise.all([
        api.get("/extra-sessions", { params: { group_id: groupId } }),
        api.get("/attendance", { params: { group_id: groupId } }),
      ]);
      setExtraSessions(rEx.data || []);
      const rows = rAtt.data || [];
      setGroupAttendance(rows);
      setReportedDates(new Set(rows.map((a) => a.date)));
    } catch { /* ignore */ }
  };

  const undoExtraSession = async (xs) => {
    try {
      await api.delete(`/extra-sessions/${xs.id}`);
      toast.success(t("undo_session_done"));
      setUndoTarget(null);
      await reloadExtrasAndAttendance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  useEffect(() => {
    (async () => {
      const [g, u, p] = await Promise.all([api.get("/groups"), api.get("/users"), api.get("/programs")]);
      setGroups(g.data); setAllUsers(u.data); setPrograms(p.data || []);
      if (!groupId && g.data.length) {
        setGroupId(g.data[0].id);
      }
    })();
  }, []);

  // Reload month-wide reported dates whenever group changes
  useEffect(() => {
    if (!groupId) return;
    setCycleOffset(0);
    (async () => {
      const att = await api.get("/attendance", { params: { group_id: groupId } });
      const rows = att.data || [];
      setGroupAttendance(rows);
      const set = new Set();
      rows.forEach((a) => set.add(a.date));
      setReportedDates(set);
    })();
    (async () => {
      try {
        const r = await api.get("/extra-sessions", { params: { group_id: groupId } });
        setExtraSessions(r.data || []);
      } catch { /* ignore */ }
    })();
    // Cancelaciones que los alumnos hicieron por la app (para el sello y el aviso).
    (async () => {
      try {
        const r = await api.get("/attendance/cancellations", { params: { group_id: groupId } });
        const byKey = {};
        (r.data?.cancellations || []).forEach((c) => { byKey[`${c.student_id}|${c.date}`] = c; });
        setCancellations(byKey);
        const pend = {};
        (r.data?.pending_overrides || []).forEach((o) => { pend[`${o.student_id}|${o.date}`] = o; });
        setPendingOverrides(pend);
      } catch { /* ignore */ }
    })();
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    setParams({ group: groupId });
    (async () => {
      const enr = await api.get("/enrollments", { params: { group_id: groupId, with_consumption: true } });
      setEnrollments(enr.data);
      const att = await api.get("/attendance", { params: { group_id: groupId, date } });
      const map = {};
      const notesMap = {};
      att.data.forEach((a) => { map[a.student_id] = a.status; notesMap[a.student_id] = a.notes || ""; });
      setRecords(map);
      setNotes(notesMap);
      baselineRef.current = { records: { ...map }, notes: { ...notesMap } };
      setDirty(false);
    })();
  }, [groupId, date]);

  const usersMap = useMemo(() => Object.fromEntries(allUsers.map((u) => [u.user_id, u])), [allUsers]);

  // Schedule-day matching for the selected group + date
  const currentGroup = useMemo(() => groups.find((g) => g.id === groupId), [groups, groupId]);
  const isScheduledDay = useMemo(() => {
    if (!currentGroup?.schedule_days?.length || !date) return null;
    const code = WEEKDAY_TO_CODE[new Date(date + "T00:00:00").getDay()];
    return currentGroup.schedule_days.includes(code);
  }, [currentGroup, date]);

  // Candado: un día que no es de clase del grupo no se puede pasar como
  // asistencia. Si hubo clase de verdad, primero se crea la sesión extra (eso
  // habilita la fecha). Las marcas ya guardadas sí se pueden corregir.
  const fueraDeHorario = useMemo(() => (
    isScheduledDay === false
    && !(extraSessions || []).some((x) => x.date === date && x.status === "active")
  ), [isScheduledDay, extraSessions, date]);

  // Students whose enrollment starts after the selected date can't be marked (their cycle hasn't begun).
  const notStarted = (e) => !!(e.start_date && date && date < e.start_date.slice(0, 10));
  // Pausa con fechas (viaje): esa clase está congelada para ese estudiante.
  const pausedOnDate = (e) => (date ? pauseOn(e.pauses, date) : null);

  const setStatus = (student_id, status) => {
    // La clase que el alumno canceló a tiempo ya está justificada: antes de
    // pisarla se le avisa a la profe de dónde viene esa justificación.
    const canc = cancellations[`${student_id}|${date}`];
    if (canc && status !== "justified") {
      setOverrideWarn({ student_id, status, cancellation: canc });
      return;
    }
    // Para justificar hay que decir CUÁNDO avisó el estudiante: con eso se mide
    // la ventana de la política (si no, se mediría desde que se pasa la lista).
    if (status === "justified" && !canc && !notifiedAt[student_id]) {
      setNotifyAsk({ student_id, when: `${date}T08:00` });
      return;
    }
    setRecords((p) => ({ ...p, [student_id]: status }));
    setDirty(true);
  };
  const setNote = (student_id, value) => {
    setNotes((p) => ({ ...p, [student_id]: value }));
    setDirty(true);
  };

  const save = async () => {
    try {
      const results = await Promise.all(
        enrollments.map((e) => {
          const status = records[e.student_id];
          if (!status || notStarted(e)) return null;
          return api.post("/attendance", {
            student_id: e.student_id,
            group_id: groupId,
            date,
            status,
            notes: notes[e.student_id] || "",
            notified_at: notifiedAt[e.student_id] || null,
          });
        }).filter(Boolean)
      );
      // Refresh reported dates for the badge list AND per-student consumption
      // so the badges next to each name update in lockstep with the header.
      const [att, enr] = await Promise.all([
        api.get("/attendance", { params: { group_id: groupId } }),
        api.get("/enrollments", { params: { group_id: groupId, with_consumption: true } }),
      ]);
      const rows = att.data || [];
      setGroupAttendance(rows);
      setEnrollments(enr.data || []);
      const set = new Set();
      rows.forEach((a) => set.add(a.date));
      setReportedDates(set);
      baselineRef.current = { records: { ...records }, notes: { ...notes } };
      setDirty(false);
      // Si alguna justificada se pasó de la cuota del 25 %, quedó esperando
      // autorización de dirección y por ahora consume la clase. Hay que
      // decírselo a la profe en el momento, no dejarla suponiendo.
      const blocked = (results || [])
        .map((r) => r?.data)
        .filter((d) => d?.quota_review);
      // Cambios que pisan una cancelación del alumno: no se aplicaron, quedaron
      // esperando que la administración los valide.
      const pend = (results || []).map((r) => r?.data).filter((d) => d?.pending_validation);
      if (pend.length) {
        const r = await api.get("/attendance/cancellations", { params: { group_id: groupId } });
        const pmap = {};
        (r.data?.pending_overrides || []).forEach((o) => { pmap[`${o.student_id}|${o.date}`] = o; });
        setPendingOverrides(pmap);
        toast.warning(
          `${pend.length === 1 ? "Un estudiante" : `${pend.length} estudiantes`} había cancelado a tiempo: `
          + "tu cambio quedó anotado y lo tiene que validar la administración antes de aplicarse.",
          { duration: 10000 },
        );
      }
      if (blocked.length) {
        const names = blocked
          .map((d) => usersMap[d.student_id]?.name || "")
          .filter(Boolean);
        // Dos motivos distintos: aviso tardío (fuera de política) o cuota agotada.
        const tarde = blocked.filter((d) => d.out_of_policy);
        if (tarde.length) {
          const n = tarde.map((d) => usersMap[d.student_id]?.name || "").filter(Boolean);
          toast.warning(
            `${n.join(", ") || "El estudiante"}: el aviso llegó con menos horas de las que pide `
            + "la política, así que la justificada queda PENDIENTE de revisión de la administración "
            + "y por ahora consume la clase.",
            { duration: 11000 },
          );
        }
        if (tarde.length < blocked.length) {
          toast.warning(t("quota_review_toast", {
            names: names.join(", "),
            used: blocked[0].quota_used ?? "",
            quota: blocked[0].cycle_quota ?? "",
          }), { duration: 9000 });
        }
      } else if (!pend.length) {
        toast.success(t("saved"));
      }
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_generic")); }
  };

  // Clear a single student's attendance for the current date. Removes the DB
  // row (returns cycle badge to "Próxima") and releases the payment counter
  // if the previous status was consuming a class.
  const clearAttendance = async (student_id) => {
    try {
      await api.delete("/attendance", { params: { student_id, group_id: groupId, date } });
      // Refresh baseline, badges and per-student consumption in one pass so
      // the UI matches server state after the deletion.
      const [att, enr] = await Promise.all([
        api.get("/attendance", { params: { group_id: groupId } }),
        api.get("/enrollments", { params: { group_id: groupId, with_consumption: true } }),
      ]);
      const rows = att.data || [];
      setGroupAttendance(rows);
      setEnrollments(enr.data || []);
      const set = new Set();
      rows.forEach((a) => set.add(a.date));
      setReportedDates(set);
      // Also drop the local status/notes for that student on the current date
      setRecords((p) => { const n = { ...p }; delete n[student_id]; return n; });
      setNotes((p) => { const n = { ...p }; delete n[student_id]; return n; });
      baselineRef.current = {
        records: (() => { const n = { ...baselineRef.current.records }; delete n[student_id]; return n; })(),
        notes: (() => { const n = { ...baselineRef.current.notes }; delete n[student_id]; return n; })(),
      };
      setClearTarget(null);
      toast.success(t("attendance_cleared"));
    } catch { toast.error(t("error_generic")); }
  };

  // Mark all enrolled students as teacher_cancelled for the current date
  const runCancelClass = async () => {
    setConfirmCancel(false);
    try {
      await Promise.all(
        enrollments.filter((e) => !notStarted(e) && !pausedOnDate(e)).map((e) =>
          api.post("/attendance", {
            student_id: e.student_id,
            group_id: groupId,
            date,
            status: "teacher_cancelled",
            notes: notes[e.student_id] || "",
          })
        )
      );
      // Reload current view
      const att = await api.get("/attendance", { params: { group_id: groupId, date } });
      const map = {}; const notesMap = {};
      att.data.forEach((a) => { map[a.student_id] = a.status; notesMap[a.student_id] = a.notes || ""; });
      setRecords(map);
      setNotes(notesMap);
      baselineRef.current = { records: { ...map }, notes: { ...notesMap } };
      setDirty(false);
      // Refresh month-wide reported set + full attendance rows
      const monthAtt = await api.get("/attendance", { params: { group_id: groupId } });
      const rows = monthAtt.data || [];
      setGroupAttendance(rows);
      const set = new Set();
      rows.forEach((a) => set.add(a.date));
      setReportedDates(set);
      toast.success(t("class_cancelled_saved"));
    } catch { toast.error(t("error_generic")); }
  };

  // Block in-app navigation when dirty (via global navigation guard)
  useEffect(() => {
    setGuard(dirty);
    return () => setGuard(false);
  }, [dirty, setGuard]);

  // Block full page unload / refresh when dirty
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Guarded handlers for in-page changes (date / group)
  const tryChangeDate = (newDate) => {
    if (dirty) {
      setPendingNav({ exec: () => { setDate(newDate); setDirty(false); } });
    } else {
      setDate(newDate);
    }
  };
  // Cambiar de ciclo tiene que arrastrar la fecha seleccionada: si no, se marca
  // asistencia de una fecha del ciclo 1 estando en la pestaña del ciclo 2
  // (corrupción silenciosa reportada el 08/09/2026).
  const tryChangeCycle = (next) => {
    const exec = () => { snapDateRef.current = true; setCycleOffset(next); };
    if (dirty) setPendingNav({ exec: () => { exec(); setDirty(false); } });
    else exec();
  };
  const tryChangeGroup = (newId) => {
    if (dirty) {
      setPendingNav({ exec: () => { setGroupId(newId); setDirty(false); } });
    } else {
      setGroupId(newId);
    }
  };

  const confirmPendingNav = async () => {
    if (pendingNav) { const fn = pendingNav.exec; setPendingNav(null); fn(); return; }
    if (pending) proceed();
  };
  const cancelPendingNav = () => {
    setPendingNav(null);
    if (pending) cancel();
  };

  const guardOpen = !!pendingNav || !!pending;

  // Build the CURRENT CYCLE's scheduled classes for the selected group
  // (matches the student's "Mi ciclo actual" view — shows every class in the cycle,
  // not just those in the current calendar month).
  const currentProgram = useMemo(
    () => programs.find((p) => p.id === currentGroup?.program_id) || null,
    [programs, currentGroup]
  );
  const scheduledClasses = useMemo(() => {
    if (!currentGroup) return [];
    // Effective cycle length: enrollment override → program default → 8
    // For private groups (single enrollment), the enrollment's own
    // `classes_included` override wins. For multi-student groups every
    // enrollment could have a different override; we pick the max so the
    // header covers the longest cycle in the room.
    const overrides = enrollments
      .map((e) => Number(e.classes_included))
      .filter((n) => n && n > 0);
    const enrolledCycle = overrides.length ? Math.max(...overrides) : 0;
    const totalPerCycle = enrolledCycle || currentProgram?.classes_included || 8;
    // Earliest enrollment start_date in this group, or group.created_at as fallback
    const enrStarts = enrollments
      .map((e) => e.start_date)
      .filter(Boolean)
      .sort();
    const startISO = enrStarts[0] || (currentGroup.created_at ? currentGroup.created_at.slice(0, 10) : null);
    if (!startISO) return [];
    // Per-student starts_on_day / plan_changes: only meaningful when the group
    // has a single student (private student). For multi-student groups every
    // student maintains their own offset — we cannot represent all of them in
    // one strip, so default to 0.
    const single = enrollments.length === 1 ? enrollments[0] : null;
    const cc = currentCycleDates({
      group: currentGroup,
      enrollment: {
        id: single?.id || "__group__",
        start_date: startISO,
        starts_on_day: Number(single?.starts_on_day || 0),
        plan_changes: single?.plan_changes || [],
        self_booking: !(currentGroup.schedule_days || []).length,
      },
      extraSessions,
      attendance: groupAttendance,
      totalPerCycle,
      scope: "group",
      // La pausa es por estudiante: solo tiñe la tira cuando el grupo es de
      // una sola persona (clase privada).
      pauses: single?.pauses || [],
    });
    if (!cc) return [];
    const startsOnDay = Number(single?.starts_on_day || 0);
    const swapMap = cc.swapMap;
    const today = todayISO();
    const targetCycle = cc.cycle + cycleOffset;
    const cycleDates = cc.classified.filter((d) => d.cycle === targetCycle);
    return cycleDates.map((d) => ({
      date: d.date,
      status: d.status,
      reported: !!d.status,
      past: d.date < today,
      today: d.date === today,
      future: d.date > today,
      // When this chip is a rescheduled date, expose the metadata so the UI
      // can render the 🔁 badge + tooltip + "undo reschedule" action.
      reschedule: swapMap[d.date] || null,
      cycle: d.cycle,
      paused: !!d.paused,
      totalPerCycle,
      // Only cycle 1 carries the "pre-existing consumption" (starts_on_day).
      preConsumed: d.cycle === 1 ? startsOnDay : 0,
    }));
  }, [currentGroup, currentProgram, enrollments, groupAttendance, cycleOffset, extraSessions]);

  // Número absoluto del ciclo mostrado (para el rótulo "Ciclo 2" en vez de "Ciclo +1").
  const shownCycleNumber = scheduledClasses[0]?.cycle ?? null;

  /* Qué sesión está detrás de una ficha del ciclo. En una clase que el estudiante
     reserva desde el calendario, el día del ciclo ES la sesión extra: sin esto el
     lápiz de arriba abría el formulario en blanco y creaba una sesión nueva en vez
     de mover la que ya existe. */
  const sessionOn = (c) =>
    c.reschedule?.meta || extraSessions.find((x) => x.date === c.date) || null;

  // Estado del DÍA a nivel de GRUPO. Antes la burbuja pintaba el color del
  // último registro que cargaba (un estudiante al azar), así que una clase con
  // todos presentes podía salir naranja por una sola justificada. Ahora la tira
  // solo dice si la asistencia se tomó, si quedó incompleta o si falta.
  const dayStats = useMemo(() => {
    const marks = new Map();
    for (const a of groupAttendance || []) {
      if (!a.date || !a.status) continue;
      const s = marks.get(a.date) || { ids: new Set(), cancelled: 0 };
      s.ids.add(a.student_id);
      if (a.status === "teacher_cancelled") s.cancelled += 1;
      marks.set(a.date, s);
    }
    const out = new Map();
    for (const c of scheduledClasses) {
      const expected = enrollments.filter(
        (e) => (!e.start_date || c.date >= e.start_date.slice(0, 10)) && !pauseOn(e.pauses, c.date),
      ).length;
      const s = marks.get(c.date);
      out.set(c.date, { expected, marked: s ? s.ids.size : 0, cancelled: s ? s.cancelled : 0 });
    }
    return out;
  }, [groupAttendance, enrollments, scheduledClasses]);

  // Clase de UN solo estudiante: la tira vuelve a los colores de estado
  // (presente / justificada / injustificada / cancelada), porque ahí el día y
  // el estudiante son lo mismo. La lectura de grupo solo aplica a 2 o más.
  const isSingleStudent = enrollments.length === 1;

  const dayTone = (c) => {
    if (c.paused) {
      return { cls: "bg-slate-200 text-slate-500 border-slate-300 border-dashed", icon: "paused", hint: t("att_day_paused") };
    }
    if (isSingleStudent) {
      if (c.status) {
        return {
          cls: attendanceChipClass(c.status),
          icon: c.status === "teacher_cancelled" ? "cancelled"
            : c.status === "present" ? "done"
            : c.status === "removed" ? "cancelled" : "partial",
          hint: t(c.status),
        };
      }
      if (c.past) return { cls: "bg-red-200 text-red-900 border-red-500", icon: "missing", hint: t("att_day_missing") };
      if (c.today) return { cls: "bg-blue-100 text-blue-900 border-blue-500", icon: "today", hint: t("att_day_missing") };
      return { cls: "bg-slate-100 text-slate-700 border-slate-900", icon: "upcoming", hint: t("upcoming") };
    }
    const s = dayStats.get(c.date) || { expected: 0, marked: 0, cancelled: 0 };
    const count = s.expected ? `${s.marked}/${s.expected} ${t("att_day_marked_count")}` : "";
    if (s.expected > 0 && s.cancelled >= s.expected) {
      return { cls: "bg-slate-500 text-white border-slate-700", icon: "cancelled", hint: t("teacher_cancelled") };
    }
    if (s.expected > 0 && s.marked >= s.expected) {
      return { cls: "bg-emerald-200 text-emerald-900 border-emerald-500", icon: "done", hint: `${t("att_day_taken")} · ${count}` };
    }
    if (s.marked > 0) {
      return { cls: "bg-amber-200 text-amber-900 border-amber-500", icon: "partial", hint: `${t("att_day_partial")} · ${count}` };
    }
    if (c.past) {
      return { cls: "bg-red-200 text-red-900 border-red-500", icon: "missing", hint: t("att_day_missing") };
    }
    if (c.today) {
      return { cls: "bg-blue-100 text-blue-900 border-blue-500", icon: "today", hint: t("att_day_missing") };
    }
    return { cls: "bg-slate-100 text-slate-700 border-slate-900", icon: "upcoming", hint: t("upcoming") };
  };

  const teacherOptions = useMemo(() => {
    const ids = [...new Set(groups.map((g) => g.teacher_id).filter(Boolean))];
    return ids
      .map((id) => ({ id, name: allUsers.find((u) => u.user_id === id)?.name || id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, allUsers]);

  const visibleGroups = useMemo(
    () => (teacherFilter === "all" ? groups : groups.filter((g) => g.teacher_id === teacherFilter)),
    [groups, teacherFilter],
  );

  // Si el grupo abierto no es de la profe filtrada, saltamos al primero que sí.
  useEffect(() => {
    if (teacherFilter === "all" || !visibleGroups.length) return;
    if (!visibleGroups.some((g) => g.id === groupId)) tryChangeGroup(visibleGroups[0].id);
  }, [teacherFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar de ciclo la fecha seleccionada salta a ese ciclo: la de hoy si
  // pertenece al ciclo mostrado, si no la primera que no haya pasado, y si el
  // ciclo ya terminó, la última. Solo actúa cuando el usuario cambió de ciclo
  // (nunca sobre una fecha escrita a mano en el campo).
  useEffect(() => {
    if (!snapDateRef.current) return;
    if (!scheduledClasses.length) return;
    snapDateRef.current = false;
    if (scheduledClasses.some((c) => c.date === date)) return;
    const today = todayISO();
    const target =
      scheduledClasses.find((c) => c.date === today) ||
      scheduledClasses.find((c) => c.date >= today) ||
      scheduledClasses[scheduledClasses.length - 1];
    if (target) setDate(target.date);
  }, [scheduledClasses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extras/reprogramaciones del ciclo mostrado: las de ciclos anteriores no se
  // repiten acá (quedan en el historial del estudiante).
  const cycleExtras = useMemo(() => {
    if (!scheduledClasses.length) return extraSessions;
    const from = scheduledClasses[0].date;
    const to = scheduledClasses[scheduledClasses.length - 1].date;
    return extraSessions.filter((x) => (x.date || "") >= from && (x.date || "") <= to);
  }, [extraSessions, scheduledClasses]);

  // Metadata for the cycle strip header
  const cycleInfo = useMemo(() => {
    if (!scheduledClasses.length) return null;
    const first = scheduledClasses[0];
    // Consumption rules (mirror `compute_consumption` on backend):
    //   present | absent | unjustified → always consumes 1
    //   justified → NEVER consumes (auto in-window or admin-approved — both free)
    //   teacher_cancelled → never consumes
    //   unreported → never consumes (yet)
    let freeUsed = 0;
    const sorted = [...scheduledClasses]
      .filter((c) => !!c.status)
      .sort((a, b) => (a.date > b.date ? 1 : -1));
    let consumed = 0;
    for (const c of sorted) {
      if (c.status === "teacher_cancelled") continue;
      if (c.status === "justified") {
        // All justified absences are free (the 25% threshold used to gate
        // this but was removed to honour the admin's explicit approval).
        freeUsed += 1;
      } else if (c.status === "present" || c.status === "absent" || c.status === "unjustified" || c.status === "unjustified_consumed") {
        consumed += 1;
      }
    }
    const reportedCount = consumed + (first.preConsumed || 0);
    return {
      cycle: first.cycle,
      total: first.totalPerCycle,
      reportedCount,
      length: scheduledClasses.length,
    };
  }, [scheduledClasses]);

  return (
    <div>
      <PageHeader
        title={t("attendance")}
        testId="attendance-header"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setExtraOpen(true)}
              disabled={!groupId}
              data-testid="add-extra-session-button"
              className="text-orange-700 border-orange-300 hover:bg-orange-50"
            >
              <CalendarPlus className="h-4 w-4" /> {t("add_extra_session")}
            </Button>
            {enrollments.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setConfirmCancel(true)}
                disabled={!groupId}
                data-testid="cancel-class-button"
                className="text-slate-700 border-slate-300"
              >
                <Ban className="h-4 w-4" /> {t("cancel_class")}
              </Button>
            )}
            <Button data-testid="save-attendance-button" onClick={save} disabled={!groupId || enrollments.length === 0 || !dirty}>
              <Save className="h-4 w-4" /> {t("save_attendance")}
              {dirty && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
            </Button>
          </div>
        }
      />

      <div className={`grid grid-cols-1 gap-3 mb-5 ${isAcademicStaff ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {isAcademicStaff && (
          <div>
            <Label>{t("teacher")}</Label>
            <Select value={teacherFilter} onValueChange={setTeacherFilter}>
              <SelectTrigger data-testid="attendance-teacher-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {teacherOptions.map((tt) => (
                  <SelectItem key={tt.id} value={tt.id}>{tt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>{t("group")}</Label>
          <Select value={groupId} onValueChange={tryChangeGroup}>
            <SelectTrigger data-testid="attendance-group-select"><SelectValue placeholder={t("select_group")} /></SelectTrigger>
            <SelectContent>
              {visibleGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("date")}</Label>
          <Input type="date" data-testid="attendance-date-input" value={date} onChange={(e) => tryChangeDate(e.target.value)} />
          {isScheduledDay !== null && (() => {
            // If the current date corresponds to an active reschedule OR an
            // extra/makeup session, treat it as legitimate and show a
            // dedicated hint instead of the misleading "off schedule" warning.
            const rs = (extraSessions || []).find((x) => x.date === date && x.status === "active");
            if (rs) {
              const label = rs.type === "reschedule"
                ? t("date_is_reschedule", { orig: formatDMY(rs.original_class_date) || "" })
                : rs.type === "makeup" ? t("date_is_makeup") : t("date_is_extra");
              return (
                <p data-testid="attendance-schedule-hint" className="text-[11px] mt-1.5 flex items-center gap-1.5 text-blue-700">
                  <Repeat className="h-3 w-3" /> {label}
                </p>
              );
            }
            return (
              <p
                data-testid="attendance-schedule-hint"
                className={`text-[11px] mt-1.5 flex items-center gap-1.5 ${
                  isScheduledDay ? "text-emerald-700" : "text-orange-700"
                }`}
              >
                {isScheduledDay ? (
                  <>
                    <CalendarCheck2 className="h-3 w-3" />
                    {t("date_in_schedule")}
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3" />
                    {t("date_off_schedule")}
                  </>
                )}
              </p>
            );
          })()}
        </div>
      </div>

      {/* Día que no es de clase: la lista queda bloqueada hasta que se cree la
          sesión extra, para que nadie reporte asistencia en una fecha que no
          existe en el programa del estudiante. */}
      {fueraDeHorario && enrollments.length > 0 && (
        <div
          className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm"
          data-testid="attendance-blocked-banner"
        >
          <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
          <p className="flex-1 min-w-0 text-orange-900">
            <span className="font-semibold">{t("att_blocked_title")}</span>{" "}
            {t("att_blocked_body")}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="text-orange-800 border-orange-300 hover:bg-orange-100"
            onClick={() => setExtraOpen(true)}
            data-testid="attendance-blocked-add-extra"
          >
            <CalendarPlus className="h-4 w-4" /> {t("add_extra_session")}
          </Button>
        </div>
      )}

      {scheduledClasses.length > 0 && (
        <div className="mb-5 bg-white border border-slate-200 rounded-lg p-4" data-testid="scheduled-classes-list">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <CalendarCheck2 className="h-4 w-4 text-brand-blue" />
              {cycleOffset === 0 ? t("my_current_cycle") : cycleOffset < 0 ? t("my_previous_cycle") : t("my_next_cycle")}
              {cycleInfo && (
                <span className="text-[11px] font-medium text-slate-500 ml-2">
                  {t("cycle")} {cycleInfo.cycle} · {cycleInfo.reportedCount}/{cycleInfo.total}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" disabled={(shownCycleNumber ?? 1) <= 1} onClick={() => tryChangeCycle(cycleOffset - 1)} data-testid="teacher-att-prev-cycle" title={t("my_previous_cycle")}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-xs font-medium text-slate-700 min-w-[70px] text-center" data-testid="teacher-att-cycle-label">
                {cycleOffset === 0
                  ? t("cycle_current")
                  : `${t("cycle")} ${shownCycleNumber ?? Math.abs(cycleOffset)}`}
              </span>
              <Button variant="outline" size="icon" onClick={() => tryChangeCycle(cycleOffset + 1)} data-testid="teacher-att-next-cycle" title={t("my_next_cycle")}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              {cycleOffset !== 0 && (
                <Button variant="ghost" size="sm" onClick={() => tryChangeCycle(0)} data-testid="teacher-att-this-cycle">
                  {t("cycle_current")}
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {scheduledClasses.map((c) => {
              const isSelected = c.date === date;
              const tone = dayTone(c);
              const cls = tone.cls;
              const selectedRing = isSelected
                ? " ring-2 ring-offset-1 ring-blue-600 border-2 font-bold"
                : "";
              return (
                /* El botón de fecha va siempre a la vista: escondido tras el hover
                   y de 16 px no se encontraba, y en tablet no hay hover. */
                <div key={c.date} className={`inline-flex items-stretch rounded-md border overflow-hidden ${cls}${selectedRing}`}>
                  <button
                    type="button"
                    onClick={() => tryChangeDate(c.date)}
                    data-testid={`scheduled-class-${c.date}`}
                    aria-pressed={isSelected}
                    className="px-2.5 py-1.5 text-[11px] font-semibold flex items-center gap-1.5"
                    title={c.reschedule
                      ? `${t("rescheduled_from", { date: formatDMY(c.reschedule.original) })} · ${tone.hint}`
                      : `${c.date} · ${tone.hint}`}
                  >
                    {c.reschedule ? <Repeat className="h-3 w-3" />
                      : tone.icon === "paused" ? <PauseCircle className="h-3 w-3" />
                      : tone.icon === "done" ? <CheckCircle2 className="h-3 w-3" />
                      : tone.icon === "partial" || tone.icon === "missing" ? <AlertTriangle className="h-3 w-3" />
                      : tone.icon === "cancelled" ? <Ban className="h-3 w-3" />
                      : <Clock className="h-3 w-3" />}
                    {formatDayLabel(c.date, lang)}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRescheduleTarget({ date: c.date, existing: sessionOn(c) }); }}
                    data-testid={`reschedule-class-${c.date}`}
                    title={sessionOn(c)?.booking_id ? t("move_booked_class_tooltip")
                      : c.reschedule ? t("reschedule_edit") : t("reschedule_session")}
                    className="px-2 border-l border-black/10 hover:bg-black/5 transition-colors flex items-center gap-1 opacity-80 hover:opacity-100"
                  >
                    <Pencil className="h-3 w-3" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">{t("change_date_short")}</span>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-500 flex-wrap">
            {isSingleStudent ? (
              <>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("present")}`} />{t("present")}</span>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("justified")}`} />{t("justified")}</span>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("unjustified")}`} />{t("unjustified")}</span>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("teacher_cancelled")}`} />{t("teacher_cancelled")}</span>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("not_counted")}`} />{t("not_counted")}</span>
                {canRemoveFromPlan && (
                  <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("removed")}`} />{t("removed")}</span>
                )}
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />{t("att_day_missing")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300 border border-dashed border-slate-400" />{t("att_day_paused")}</span>
                <span className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass("")}`} />{t("upcoming")}</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />{t("att_day_taken")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />{t("att_day_partial")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />{t("att_day_missing")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-600" />{t("teacher_cancelled")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300 border border-dashed border-slate-400" />{t("att_day_paused")}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-200 border border-slate-900" />{t("upcoming")}</span>
              </>
            )}
          </div>
        </div>
      )}

      {cycleExtras.length > 0 && (
        <div className="mb-5 bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="extra-sessions-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-orange-800 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> {t("extra_sessions_label")}
            </h3>
            <span className="text-[11px] text-orange-700">{cycleExtras.length}</span>
          </div>
          <p className="text-[11px] text-orange-700/90 -mt-1.5 mb-2.5">{t("extra_sessions_hint")}</p>
          <div className="flex flex-wrap gap-1.5">
            {cycleExtras.map((x) => {
              const isSelected = x.date === date;
              const cls = isSelected
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-orange-800 border-orange-300 hover:bg-orange-100";
              const label =
                x.type === "reschedule"
                  ? t("extra_session_reschedule")
                  : x.type === "makeup"
                  ? t("extra_session_makeup")
                  : t("extra_session_extra");
              const divisor = isSelected ? "border-orange-400/70" : "border-orange-300";
              const accion = isSelected
                ? "text-white/90 hover:bg-orange-700"
                : "text-orange-700 hover:bg-orange-100";
              return (
                /* Los dos botones de acción van SIEMPRE a la vista: cuando aparecían
                   solo al pasar el mouse, y de 16 px, nadie los encontraba (y en
                   tablet no hay "pasar el mouse"). */
                <div key={x.id} className={`inline-flex items-stretch rounded-md border overflow-hidden ${cls}`}>
                  <button
                    type="button"
                    onClick={() => tryChangeDate(x.date)}
                    data-testid={`extra-session-${x.id}`}
                    className="px-2.5 py-1.5 text-[11px] font-medium flex items-center gap-1.5"
                    title={x.notes || `${label} · ${x.date}`}
                  >
                    {x.type === "reschedule" ? <Repeat className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                    <span className="font-semibold">{label}</span>
                    <span>{formatDayLabel(x.date, lang)}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRescheduleTarget({ date: x.date, existing: x }); }}
                    data-testid={`edit-extra-session-${x.id}`}
                    title={x.booking_id ? t("move_booked_class_tooltip") : t("edit_session_tooltip")}
                    className={`px-2 border-l ${divisor} ${accion} transition-colors flex items-center gap-1`}
                  >
                    <Pencil className="h-3 w-3" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">{t("change_date_short")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setUndoTarget(x); }}
                    data-testid={`undo-extra-session-${x.id}`}
                    title={t("undo_session_tooltip")}
                    className={`px-1.5 border-l ${divisor} ${isSelected ? "text-white/90 hover:bg-red-600" : "text-orange-700 hover:bg-red-50 hover:text-red-600"} transition-colors flex items-center`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("student")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("notes")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrollments.length === 0 && (
              <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {enrollments.map((e) => {
              const s = usersMap[e.student_id];
              const initials = (s?.name || "??").slice(0, 2).toUpperCase();
              const cur = records[e.student_id];
              return (
                <TableRow key={e.student_id} data-testid={`attendance-row-${e.student_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <BrandAvatar src={s?.picture} alt={s?.name} className="h-8 w-8" />
                      <span className="font-medium">{s?.name || e.student_id}</span>
                      {s?.unlimited_justified && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
                          title={t("unlimited_justified_hint")}
                          data-testid={`unlimited-badge-${e.student_id}`}
                        >
                          {t("unlimited_justified_badge")}
                        </span>
                      )}
                      {e.flexible_schedule && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-dashed border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700"
                          title={t("flexible_schedule_hint")}
                          data-testid={`flexible-badge-${e.student_id}`}
                        >
                          {t("flexible_schedule_badge")}
                        </span>
                      )}
                      {/* Per-student progress badge: this student's own
                          consumed/total sessions. In group classes each
                          learner can be at a different point in the cycle,
                          so showing it here avoids the confusion where the
                          group-wide header didn't match anyone. */}
                      {(() => {
                        const c = e.consumption;
                        if (!c || !c.total) return null;
                        // El chip refleja el ciclo ABIERTO: si el total consumido es
                        // múltiplo exacto del ciclo, el anterior ya cerró → 0/N.
                        const totalConsumed = c.consumed_total ?? c.consumed ?? 0;
                        const inCycle = c.total > 0 ? totalConsumed % c.total : c.consumed;
                        const pct = c.total > 0 ? Math.round((inCycle / c.total) * 100) : 0;
                        const tone =
                          pct >= 100 ? "bg-red-100 text-red-800"
                          : pct >= 75 ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800";
                        return (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}
                            title={`${inCycle}/${c.total} sesiones consumidas (${pct}%)`}
                            data-testid={`student-progress-${e.student_id}`}
                          >
                            {inCycle}/{c.total}
                          </span>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    {notStarted(e) ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold text-slate-500"
                        title={t("not_started_hint")}
                        data-testid={`attendance-not-started-${e.student_id}`}
                      >
                        {t("starts_on_date", { date: formatDMY(e.start_date.slice(0, 10), lang) })}
                      </span>
                    ) : (pausedOnDate(e) && !isSuper) ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500"
                        title={t("att_paused_hint")}
                        data-testid={`attendance-paused-${e.student_id}`}
                      >
                        <PauseCircle className="h-3 w-3" /> {t("att_paused_badge")}
                      </span>
                    ) : (fueraDeHorario && !baselineRef.current.records[e.student_id]
                         && !e.flexible_schedule) ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-orange-300 bg-orange-50 px-3 py-1 text-[11px] font-semibold text-orange-700"
                        title={t("att_blocked_body")}
                        data-testid={`attendance-blocked-${e.student_id}`}
                      >
                        <AlertTriangle className="h-3 w-3" /> {t("att_blocked_badge")}
                      </span>
                    ) : (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {/* Sello: el alumno canceló a tiempo desde la app. */}
                      {cancellations[`${e.student_id}|${date}`] && (
                        <span className="w-full inline-flex items-center gap-1.5 text-[10px] font-semibold
                                         text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-1"
                              data-testid={`student-cancelled-badge-${e.student_id}`}>
                          <CheckCircle2 className="h-3 w-3 shrink-0" />
                          Justificada: el estudiante canceló el{" "}
                          {formatDMY((cancellations[`${e.student_id}|${date}`].submitted_at_cr || "").slice(0, 10), lang)}
                          {" a las "}
                          {(cancellations[`${e.student_id}|${date}`].submitted_at_cr || "").slice(11, 16)}
                          {cancellations[`${e.student_id}|${date}`].hours_notice != null &&
                            ` · ${Math.round(cancellations[`${e.student_id}|${date}`].hours_notice)} h antes`}
                        </span>
                      )}
                      {pendingOverrides[`${e.student_id}|${date}`] && (
                        <span className="w-full text-[10px] font-semibold text-amber-800 bg-amber-50
                                         border border-amber-200 rounded px-2 py-1 mb-1"
                              data-testid={`override-pending-badge-${e.student_id}`}>
                          Tu cambio a «{pendingOverrides[`${e.student_id}|${date}`].to_status}» está
                          esperando validación de la administración.
                        </span>
                      )}
                      {pausedOnDate(e) && isSuper && (
                        <span className="w-full text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1"
                              data-testid={`attendance-paused-warning-${e.student_id}`}>
                          {t("att_paused_admin_warning")}
                        </span>
                      )}
                      {[
                        ...STATUSES,
                        ...(e.flexible_schedule && canMarkNotCounted ? ["not_counted"] : []),
                        ...(canRemoveFromPlan ? ["removed"] : []),
                      ].map((st) => (
                        <button
                          key={st}
                          data-testid={`attendance-toggle-${st}-${e.student_id}`}
                          onClick={() => setStatus(e.student_id, st)}
                          title={st === "removed" ? t("removed_hint") : undefined}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide border transition-all ${
                            cur === st
                              ? st === "present" ? "bg-green-600 text-white border-green-600"
                                : st === "absent" ? "bg-red-600 text-white border-red-600"
                                : st === "justified" ? "bg-yellow-500 text-white border-yellow-500"
                                : st === "teacher_cancelled" ? "bg-slate-600 text-white border-slate-600"
                                : st === "not_counted" ? "bg-indigo-600 text-white border-indigo-600"
                                : st === "removed" ? "bg-slate-800 text-white border-slate-800 line-through"
                                : "bg-red-900 text-white border-red-900"
                              : st === "not_counted"
                                ? "bg-white text-indigo-700 border-dashed border-indigo-300 hover:bg-indigo-50"
                                : st === "removed"
                                  ? "bg-white text-slate-500 border-dashed border-slate-400 hover:bg-slate-100"
                                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {t(st)}
                        </button>
                      ))}
                      {/* "Sin reportar": only shown when the student has a
                          saved status for this date (i.e. it exists in the
                          baseline). Clicking it opens a confirmation dialog
                          that deletes the DB row and releases the cycle. */}
                      {baselineRef.current.records[e.student_id] && (
                        <button
                          type="button"
                          data-testid={`attendance-clear-${e.student_id}`}
                          onClick={() => setClearTarget({ student_id: e.student_id, name: s?.name || e.student_id })}
                          title={t("clear_attendance_tooltip")}
                          className="px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 hover:text-slate-700 hover:border-slate-400 inline-flex items-center gap-1.5 transition-all"
                        >
                          <Eraser className="h-3 w-3" /> {t("clear_attendance")}
                        </button>
                      )}
                    </div>
                    )}
                    {cur && !notStarted(e) && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <StatusBadge status={cur} />
                        {cur === "unjustified_consumed" && (
                          <span className="text-[10px] font-semibold text-amber-700"
                                data-testid={`awaiting-approval-${e.student_id}`}>
                            {t("awaiting_approval")}
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      data-testid={`attendance-notes-${e.student_id}`}
                      value={notes[e.student_id] || ""}
                      onChange={(ev) => setNote(e.student_id, ev.target.value)}
                      placeholder="…"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Unsaved-changes guard dialog */}
      <Dialog open={guardOpen} onOpenChange={(o) => { if (!o) cancelPendingNav(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("unsaved_changes_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("unsaved_changes_body")}</p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={cancelPendingNav} data-testid="unsaved-cancel-button">
              {t("stay_here")}
            </Button>
            <Button
              onClick={async () => { await save(); confirmPendingNav(); }}
              data-testid="unsaved-save-and-continue-button"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="h-4 w-4" /> {t("save_and_continue")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmPendingNav}
              data-testid="unsaved-discard-button"
            >
              {t("discard_changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel-class confirmation */}
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("cancel_class")}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {t("cancel_class_body", { count: enrollments.length, date })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(false)}>{t("cancel")}</Button>
            <Button
              variant="destructive"
              onClick={runCancelClass}
              data-testid="cancel-class-confirm-button"
            >
              <Ban className="h-4 w-4" /> {t("cancel_class_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear-attendance confirmation */}
      <Dialog open={!!clearTarget} onOpenChange={(o) => { if (!o) setClearTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="clear-attendance-dialog">
          <DialogHeader>
            <DialogTitle>{t("clear_attendance_confirm_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <strong>{clearTarget?.name}</strong> — {t("clear_attendance_confirm_body", { date })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearTarget(null)}>{t("cancel")}</Button>
            <Button
              onClick={() => clearTarget && clearAttendance(clearTarget.student_id)}
              data-testid="clear-attendance-confirm-button"
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Eraser className="h-4 w-4" /> {t("clear_attendance_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aviso antes de pisar una cancelación que el alumno hizo a tiempo */}
      <Dialog open={!!overrideWarn} onOpenChange={(o) => { if (!o) setOverrideWarn(null); }}>
        <DialogContent className="max-w-md" data-testid="override-warning-dialog">
          <DialogHeader>
            <DialogTitle>Este estudiante canceló a tiempo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              <strong>{usersMap[overrideWarn?.student_id]?.name || ""}</strong> canceló esta clase
              desde la aplicación el{" "}
              <strong>
                {formatDMY((overrideWarn?.cancellation?.submitted_at_cr || "").slice(0, 10), lang)}
                {" a las "}
                {(overrideWarn?.cancellation?.submitted_at_cr || "").slice(11, 16)}
              </strong>
              {overrideWarn?.cancellation?.hours_notice != null &&
                ` (${Math.round(overrideWarn.cancellation.hours_notice)} h de anticipación)`}
              , así que la ausencia ya está <strong>justificada</strong> y no le consume la clase.
            </p>
            {overrideWarn?.cancellation?.reason && (
              <p className="text-xs text-slate-500">Motivo: {overrideWarn.cancellation.reason}</p>
            )}
            <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-900">
              Si de todas formas necesitás cambiarlo, el cambio queda anotado y{" "}
              <strong>no se aplica hasta que la administración lo valide</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideWarn(null)}
                    data-testid="override-warning-keep">
              Dejar justificada
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="override-warning-continue"
              onClick={() => {
                setRecords((p) => ({ ...p, [overrideWarn.student_id]: overrideWarn.status }));
                setDirty(true);
                setOverrideWarn(null);
              }}
            >
              Pedir el cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ¿Cuándo avisó el estudiante? Con eso se mide la ventana de la política */}
      <Dialog open={!!notifyAsk} onOpenChange={(o) => { if (!o) setNotifyAsk(null); }}>
        <DialogContent className="max-w-md" data-testid="notified-at-dialog">
          <DialogHeader>
            <DialogTitle>¿Cuándo avisó el estudiante?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              <strong>{usersMap[notifyAsk?.student_id]?.name || ""}</strong> — para justificar la
              ausencia hay que saber con cuánta anticipación avisó. Se mide contra la hora de la
              clase, no contra el momento en que pasás lista.
            </p>
            <div>
              <Label className="text-xs">Fecha y hora del aviso (hora de Costa Rica)</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                data-testid="notified-at-input"
                value={notifyAsk?.when || ""}
                onChange={(e) => setNotifyAsk((p) => ({ ...p, when: e.target.value }))}
              />
            </div>
            <p className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              Si avisó con menos horas de las que pide su política, la justificada queda
              <strong> pendiente de revisión</strong> de la administración.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              data-testid="notified-at-unknown"
              onClick={() => {
                setNotifiedAt((p) => ({ ...p, [notifyAsk.student_id]: "" }));
                setRecords((p) => ({ ...p, [notifyAsk.student_id]: "justified" }));
                setDirty(true);
                setNotifyAsk(null);
              }}
            >
              No avisó / no sé
            </Button>
            <Button
              data-testid="notified-at-confirm"
              onClick={() => {
                setNotifiedAt((p) => ({ ...p, [notifyAsk.student_id]: notifyAsk.when || "" }));
                setRecords((p) => ({ ...p, [notifyAsk.student_id]: "justified" }));
                setDirty(true);
                setNotifyAsk(null);
              }}
            >
              Justificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <ExtraSessionDialog
        open={extraOpen}
        onOpenChange={setExtraOpen}
        groupId={groupId}
        defaultDate={date}
        enrollments={enrollments}
        usersMap={usersMap}
        onCreated={async () => {
          try {
            const r = await api.get("/extra-sessions", { params: { group_id: groupId } });
            setExtraSessions(r.data || []);
          } catch { /* ignore */ }
        }}
      />

      {/* Reschedule dialog — opened from the ✎ button on each chip.
          Reuses ExtraSessionDialog in `preset` mode so the same backend
          endpoint (POST /extra-sessions type=reschedule) is used. */}
      <ExtraSessionDialog
        open={!!rescheduleTarget}
        onOpenChange={(o) => { if (!o) setRescheduleTarget(null); }}
        groupId={groupId}
        defaultDate={rescheduleTarget?.existing?.date || rescheduleTarget?.date || date}
        enrollments={enrollments}
        usersMap={usersMap}
        preset={rescheduleTarget ? { type: rescheduleTarget.existing?.type || "reschedule", original_class_date: rescheduleTarget.existing?.original_class_date || rescheduleTarget.date } : null}
        existing={rescheduleTarget?.existing || null}
        onRequestUndo={(xs) => { setRescheduleTarget(null); setUndoTarget(xs); }}
        onCreated={async () => {
          setRescheduleTarget(null);
          await reloadExtrasAndAttendance();
        }}
      />

      <Dialog open={!!undoTarget} onOpenChange={(o) => { if (!o) setUndoTarget(null); }}>
        <DialogContent data-testid="undo-extra-session-dialog">
          <DialogHeader>
            <DialogTitle>{t("undo_session_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {undoTarget?.type === "reschedule"
              ? t("undo_reschedule_body", {
                  date: formatDMY(undoTarget?.date),
                  orig: formatDMY(undoTarget?.original_class_date),
                })
              : t("undo_extra_body", { date: formatDMY(undoTarget?.date) })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)}>{t("cancel")}</Button>
            <Button
              onClick={() => undoTarget && undoExtraSession(undoTarget)}
              data-testid="undo-extra-session-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {t("undo_session_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
